import { Button, CircularProgress, SxProps, useTheme, Box, IconButton, Link } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext } from 'react-hook-form';
import { useOne } from '@refinedev/core';

type Props = {
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    isUploading?: boolean;
    allowMultiple?: boolean;
    uploadProgress?: number;
    size?: 'large' | 'small';
    className?: string;
    sx?: SxProps;
    type?: 'button' | 'hyperlink';
    pdfFieldName?: string; // Field name for PDF in form context
}

export const UploadMediaButton = ({ 
    onInputChange, 
    isUploading, 
    allowMultiple = true, 
    uploadProgress = 0, 
    size = 'large', 
    className, 
    sx,
    type = 'button',
    pdfFieldName = 'pdf_catalog_media_id'
}: Props) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  
  // Try to get form context, but don't fail if it doesn't exist
  let formContext;
  try {
    formContext = useFormContext();
  } catch {
    formContext = null;
  }

  // Use form context if available, otherwise use local state
  const pdfMediaId = formContext?.watch(pdfFieldName);
  
  const uploadButtonIcon = isUploading ? 
    <CircularProgress size={20} variant="determinate" value={uploadProgress} /> : 
    <CloudUploadIcon fontSize={size === 'small' ? 'large' : 'small'}/>;

    const handleDeletePdf = () => {
    if (formContext) {
      formContext.setValue(pdfFieldName, null, {shouldDirty: true});
    }
  };

  const { data: pdfMedia } = useOne({
    resource: "medias",
    id: pdfMediaId,
    queryOptions: {
      enabled: !!pdfMediaId,
    }
  });

  // If hyperlink type and PDF is uploaded, show preview
  if (type === 'hyperlink' && pdfMediaId) {
    return (
      <Box sx={{ position: 'relative', width: '50%', height: '600px', margin: '0 auto', ...sx }}>
        <IconButton
          onClick={handleDeletePdf}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <iframe
          src={pdfMedia?.data.cdn_url}
          width="100%"
          height="100%"
          style={{ border: 'none', borderRadius: '4px' }}
          title="PDF Preview"
        />
      </Box>
    );
  }

  // Hyperlink mode rendering
  if (type === 'hyperlink') {
    return (
      <Box sx={{ ...sx }}>
        <Link
          component="label"
          sx={{
            cursor: 'pointer',
            color: isDarkMode ? 'primary.light' : 'primary.main',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
          className={className}
        >
          {uploadButtonIcon}
          {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload PDF'}
          <input
            type="file"
            accept="application/pdf"
            onChange={onInputChange}
            disabled={isUploading}
            hidden
          />
        </Link>
      </Box>
    );
  }

  // Default button mode rendering
  return (
    <Button
        component="label"
        variant="outlined"
        disabled={isUploading}
        sx={{ 
            py: 3,
            border: '1px solid',
            borderColor: isDarkMode ? 'grey.700' : 'grey.400',
            color: isDarkMode ? 'grey.300' : 'grey.600',
            backgroundColor: 'transparent',
            '&:hover': {
                borderColor: isDarkMode ? 'grey.100' : 'grey.900',
                backgroundColor: 'transparent'
            },
            width: '100%',
            gap: 1,
            ...sx
        }}
        className={className}
    >
        {uploadButtonIcon}
        {size === 'large' && (isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Media')}
        <input
            type="file"
            multiple={allowMultiple}
            accept="image/*,video/*"
            onChange={onInputChange}
            disabled={isUploading}
            hidden
        />
    </Button>
  )
}

export default UploadMediaButton